import React, { useState, useRef, useEffect } from "react";

// Alternativa para lidar com SVGs sem depender de bibliotecas externas
const CalendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
);

const ChevronLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
);

const ChevronRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6"></polyline>
    </svg>
);

const ChevronDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
);

// Interface para as props do componente
interface DateRangePickerProps {
    onChange?: (range: { startDate: Date | null; endDate: Date | null }) => void;
    initialStartDate?: Date | null;
    initialEndDate?: Date | null;
    placeholder?: string;
}

// Componente principal DateRangePicker
const DateRangePicker: React.FC<DateRangePickerProps> = ({
    onChange,
    initialStartDate = null,
    initialEndDate = null,
    placeholder = "Selecione um período"
}) => {
    // Helper para calcular range baseado no período relativo informado
    function getRangeForPeriod(period: string): { start: Date; end: Date } | null {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let start: Date | null = null;
        let end: Date | null = null;

        switch (period) {
            case "hoje":
                start = new Date(today);
                end = new Date(today);
                break;
            case "ontem":
                start = new Date(today);
                start.setDate(today.getDate() - 1);
                start.setHours(0, 0, 0, 0);
                end = new Date(today);
                end.setDate(today.getDate() - 1);
                end.setHours(23, 59, 59, 999);
                break;
            case "ultimos7":
                start = new Date(today);
                start.setDate(today.getDate() - 6);
                end = new Date(today);
                break;
            case "ultimos14":
                start = new Date(today);
                start.setDate(today.getDate() - 13);
                end = new Date(today);
                break;
            case "ultimos30":
                start = new Date(today);
                start.setDate(today.getDate() - 29);
                end = new Date(today);
                break;
            case "estaSemana":
                const dayOfWeek = today.getDay();
                const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                start = new Date(today);
                start.setDate(today.getDate() - diff);
                end = new Date(today);
                break;
            case "semanaPassada":
                const lastWeekEnd = new Date(today);
                const dayOfWeek2 = today.getDay();
                const diff2 = dayOfWeek2 === 0 ? 6 : dayOfWeek2 - 1;
                lastWeekEnd.setDate(today.getDate() - diff2 - 1);
                start = new Date(lastWeekEnd);
                start.setDate(lastWeekEnd.getDate() - 6);
                end = lastWeekEnd;
                break;
            case "esteMes":
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end = new Date(today);
                break;
            case "mesPassado":
                start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                end = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case "esteAno":
                start = new Date(today.getFullYear(), 0, 1);
                end = new Date(today);
                break;
            case "anoPassado":
                start = new Date(today.getFullYear() - 1, 0, 1);
                end = new Date(today.getFullYear() - 1, 11, 31);
                break;
            case "todoTempo":
                start = new Date(2025, 6, 1);
                end = new Date(today);
                break;
            default:
                return null;
        }

        return start && end ? { start, end } : null;
    }
    // Carregar datas salvas no localStorage ou usar os valores iniciais
    const getSavedDates = () => {
        try {
            const savedRange = localStorage.getItem('dateRangeSelection');
            if (savedRange) {
                const { startDate, endDate } = JSON.parse(savedRange);
                const period = localStorage.getItem('dateRangePeriod') || "ultimos7";
                // Se for período relativo, recomputa para a data atual
                if (period !== 'custom') {
                    const computed = getRangeForPeriod(period);
                    if (computed) {
                        return {
                            startDate: computed.start,
                            endDate: computed.end,
                            period
                        };
                    }
                }
                // Caso seja custom, mantém exatamente o que estava salvo
                return {
                    startDate: startDate ? new Date(startDate) : null,
                    endDate: endDate ? new Date(endDate) : null,
                    period
                };
            }
        } catch (error) {
            console.error("Erro ao recuperar datas do localStorage:", error);
        }

        // Se não houver dados salvos ou houver erro, definir hoje
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return {
            startDate: initialStartDate || today,
            endDate: initialEndDate || today,
            period: "hoje"
        };
    };

    const savedDates = getSavedDates();

    // Estados do componente
    const [isOpen, setIsOpen] = useState(false);
    const [startDate, setStartDate] = useState<Date | null>(savedDates.startDate);
    const [endDate, setEndDate] = useState<Date | null>(savedDates.endDate);
    const [hoverDate, setHoverDate] = useState<Date | null>(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selecting, setSelecting] = useState<"start" | "end" | null>(null);
    const [activePeriod, setActivePeriod] = useState<string>(savedDates.period);

    // Refs para detectar cliques fora do componente
    const datePickerRef = useRef<HTMLDivElement>(null);

    // Função para detectar cliques fora do componente
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        // Adicionar event listener apenas quando o calendário estiver aberto
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        // Limpar event listener ao desmontar ou quando o calendário fechar
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Autoatualiza à meia-noite quando período for relativo (não custom)
    useEffect(() => {
        if (activePeriod === 'custom') return;

        const now = new Date();
        const nextMidnight = new Date(now);
        nextMidnight.setDate(now.getDate() + 1);
        nextMidnight.setHours(0, 0, 0, 0);
        const msUntilMidnight = nextMidnight.getTime() - now.getTime();

        const timer = setTimeout(() => {
            const computed = getRangeForPeriod(activePeriod);
            if (computed) {
                setStartDate(computed.start);
                setEndDate(computed.end);
                // Persistir
                localStorage.setItem('dateRangeSelection', JSON.stringify({
                    startDate: computed.start.toISOString(),
                    endDate: computed.end.toISOString()
                }));
                localStorage.setItem('dateRangePeriod', activePeriod);
                // Notificar
                if (onChange) onChange({ startDate: computed.start, endDate: computed.end });
            }
        }, Math.max(0, msUntilMidnight));

        return () => clearTimeout(timer);
    }, [activePeriod, onChange]);

    // Formatar data para exibição
    const formatDate = (date: Date | null): string => {
        if (!date) return "";
        return date.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
        });
    };

    // Texto a ser exibido no botão
    const displayText = startDate && endDate
        ? `${formatDate(startDate)} - ${formatDate(endDate)}`
        : startDate
            ? formatDate(startDate)
            : placeholder;

    // Manipular clique em uma data
    const handleDateClick = (date: Date) => {
        if (!selecting || selecting === "start") {
            setStartDate(date);
            setEndDate(null);
            setSelecting("end");
            setActivePeriod("custom"); // Definir como seleção personalizada
        } else {
            if (startDate) {
                let newStartDate: Date | null = startDate;
                let newEndDate: Date | null = date;

                // Garantir que a data final seja após a data inicial
                if (date < startDate) {
                    newEndDate = startDate;
                    newStartDate = date;
                }

                setStartDate(newStartDate);
                setEndDate(newEndDate);
                setActivePeriod("custom"); // Definir como seleção personalizada

                // Salvar no localStorage
                localStorage.setItem('dateRangeSelection', JSON.stringify({
                    startDate: newStartDate.toISOString(),
                    endDate: newEndDate.toISOString()
                }));
                localStorage.setItem('dateRangePeriod', 'custom');

                // Notificar mudança se callback existir
                if (onChange) {
                    onChange({
                        startDate: newStartDate,
                        endDate: newEndDate
                    });
                }

                // Fechar o calendário quando a segunda data for selecionada
                setIsOpen(false);
            } else {
                // Se por algum motivo startDate for nulo, definimos ambos
                setStartDate(date);
                setEndDate(date);
                setActivePeriod("custom"); // Definir como seleção personalizada

                // Salvar no localStorage
                localStorage.setItem('dateRangeSelection', JSON.stringify({
                    startDate: date.toISOString(),
                    endDate: date.toISOString()
                }));
                localStorage.setItem('dateRangePeriod', 'custom');

                if (onChange) {
                    onChange({
                        startDate: date,
                        endDate: date
                    });
                }

                // Fechar o calendário
                setIsOpen(false);
            }

            setSelecting(null);
        }
    };

    // Verificar se uma data está no intervalo selecionado
    const isInRange = (date: Date): boolean => {
        if (!startDate) return false;
        if (!endDate && !hoverDate) return false;

        const end = endDate || hoverDate;
        if (!end) return false;

        return date > startDate && date < end;
    };

    // Verificar se é a data de início
    const isStartDate = (date: Date): boolean => {
        return startDate ? date.toDateString() === startDate?.toDateString() : false;
    };

    // Verificar se é a data de fim
    const isEndDate = (date: Date): boolean => {
        return endDate ? date.toDateString() === endDate.toDateString() : false;
    };

    // Determinar a classe CSS para uma célula de data
    const getDateCellClass = (date: Date): string => {
        const baseClass = "w-8 h-8 rounded-full flex items-center justify-center text-sm relative mx-auto";
        const today = new Date().toDateString() === date.toDateString();

        if (isStartDate(date) || isEndDate(date)) {
            return `${baseClass} bg-blue-600 text-white hover:bg-blue-700`;
        }

        if (isInRange(date)) {
            return `${baseClass} bg-blue-100 text-blue-800 hover:bg-blue-200`;
        }

        if (today) {
            return `${baseClass} border border-blue-400 hover:bg-gray-100`;
        }

        return `${baseClass} hover:bg-gray-100`;
    };

    // Limpar seleção
    const clearSelection = () => {
        setStartDate(null);
        setEndDate(null);
        setSelecting(null);
        if (onChange) {
            onChange({ startDate: null, endDate: null });
        }
    };

    // Gerar dias para um mês específico
    const generateDaysForMonth = (monthDate: Date) => {
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();

        // Obter dia da semana do primeiro dia ajustando para semana começando na segunda-feira
        // getDay(): 0=Dom,1=Seg,...,6=Sáb -> Ajuste: 0=Seg,1=Ter,...,6=Dom
        const firstDayOfWeek = (firstDay.getDay() + 6) % 7;

        const days = [];

        // Dias do mês anterior para preencher a primeira semana
        const prevMonthLastDate = new Date(year, month, 0).getDate();
        for (let i = firstDayOfWeek - 1; i >= 0; i--) {
            const dayNum = prevMonthLastDate - i;
            days.push(
                <button
                    key={`prev-${year}-${month}-${dayNum}`}
                    type="button"
                    className="w-8 h-8 text-gray-400 opacity-50"
                    disabled
                >
                    {dayNum}
                </button>
            );
        }

        // Dias do mês atual
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            days.push(
                <button
                    key={`current-${month}-${day}`}
                    type="button"
                    className={getDateCellClass(date)}
                    onClick={() => handleDateClick(date)}
                    onMouseEnter={() => selecting === "end" && setHoverDate(date)}
                    onMouseLeave={() => selecting === "end" && setHoverDate(null)}
                >
                    {day}
                </button>
            );
        }

        // Completar com dias do próximo mês se necessário
        const totalCells = Math.ceil((firstDayOfWeek + daysInMonth) / 7) * 7;
        const remainingCells = totalCells - (firstDayOfWeek + daysInMonth);

        for (let i = 1; i <= remainingCells; i++) {
            days.push(
                <button
                    key={`next-${month}-${i}`}
                    type="button"
                    className="w-8 h-8 text-gray-400 opacity-50"
                    disabled
                >
                    {i}
                </button>
            );
        }

        return days;
    };

    // Navegar para o mês anterior
    const goToPreviousMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    };

    // Navegar para o próximo mês
    const goToNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    };

    // Definir períodos predefinidos
    const setPredefinedPeriod = (period: string) => {
        const computed = getRangeForPeriod(period);

        if (computed) {
            // Salvar no localStorage
            localStorage.setItem('dateRangeSelection', JSON.stringify({
                startDate: computed.start.toISOString(),
                endDate: computed.end.toISOString()
            }));
            localStorage.setItem('dateRangePeriod', period);

            setStartDate(computed.start);
            setEndDate(computed.end);
            setActivePeriod(period);

            if (onChange) {
                onChange({ startDate: computed.start, endDate: computed.end });
            }
        }

        // Fechar o calendário quando um período rápido for selecionado
        setIsOpen(false);
    };

    // Nomes dos dias da semana
    const weekDays = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

    // Salvar seleção no localStorage
    useEffect(() => {
        if (startDate && endDate) {
            localStorage.setItem('dateRangeSelection', JSON.stringify({ startDate: startDate.toISOString(), endDate: endDate.toISOString() }));
            localStorage.setItem('dateRangePeriod', activePeriod);
        }
    }, [startDate, endDate, activePeriod]);

    return (
        <div ref={datePickerRef} className="relative inline-block">
            {/* Botão principal */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 h-11 w-full md:w-72 rounded-lg border border-gray-200 bg-transparent py-3 px-6 text-base shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:focus:border-brand-800"
            >
                <CalendarIcon />
                <span className="whitespace-nowrap overflow-hidden text-ellipsis">{displayText}</span>
                <ChevronDownIcon />
            </button>

            {/* Dropdown do calendário */}
            {isOpen && (
                <div className="absolute right-0 z-10 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-auto dark:text-gray-400 dark:border-gray-800 dark:bg-white dark:ring-gray-800 dark:ring-2">
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Opções de período predefinidas */}
                        <div className="md:w-40 mb-4 md:mb-0">
                            <ul className="space-y-2 max-h-[280px] overflow-y-auto pr-2">
                                <li>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 rounded ${activePeriod === "hoje" ? "text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => setPredefinedPeriod("hoje")}
                                    >
                                        Hoje
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 rounded ${activePeriod === "ontem" ? "text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => setPredefinedPeriod("ontem")}
                                    >
                                        Ontem
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 rounded ${activePeriod === "ultimos7" ? "text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => setPredefinedPeriod("ultimos7")}
                                    >
                                        Últimos 7 dias
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 rounded ${activePeriod === "ultimos14" ? "text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => setPredefinedPeriod("ultimos14")}
                                    >
                                        Últimos 14 dias
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 rounded ${activePeriod === "ultimos30" ? "text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => setPredefinedPeriod("ultimos30")}
                                    >
                                        Últimos 30 dias
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 rounded ${activePeriod === "estaSemana" ? "text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => setPredefinedPeriod("estaSemana")}
                                    >
                                        Esta semana
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 rounded ${activePeriod === "semanaPassada" ? "text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => setPredefinedPeriod("semanaPassada")}
                                    >
                                        Semana passada
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 rounded ${activePeriod === "esteMes" ? "text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => setPredefinedPeriod("esteMes")}
                                    >
                                        Este mês
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 rounded ${activePeriod === "mesPassado" ? "text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => setPredefinedPeriod("mesPassado")}
                                    >
                                        Mês passado
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 rounded ${activePeriod === "esteAno" ? "text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => setPredefinedPeriod("esteAno")}
                                    >
                                        Este ano
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 rounded ${activePeriod === "anoPassado" ? "text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => setPredefinedPeriod("anoPassado")}
                                    >
                                        Ano passado
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 rounded ${activePeriod === "todoTempo" ? "text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => setPredefinedPeriod("todoTempo")}
                                    >
                                        Tempo todo
                                    </button>
                                </li>
                            </ul>
                        </div>

                        {/* Calendário duplo */}
                        <div className="flex flex-col md:flex-row gap-4">
                            {/* Primeiro calendário */}
                            <div className="w-72">
                                {/* Cabeçalho do primeiro calendário */}
                                <div className="flex items-center justify-between mb-4">
                                    <button
                                        type="button"
                                        onClick={goToPreviousMonth}
                                        className="p-1 rounded-full hover:bg-gray-100"
                                    >
                                        <ChevronLeftIcon />
                                    </button>

                                    <h3 className="text-base font-medium">
                                        {currentMonth.toLocaleDateString("pt-BR", {
                                            month: "long",
                                            year: "numeric",
                                        })}
                                    </h3>

                                    <button
                                        type="button"
                                        className="invisible p-1 rounded-full"
                                    >
                                        <ChevronRightIcon />
                                    </button>
                                </div>

                                {/* Dias da semana */}
                                <div className="grid grid-cols-7 mb-2">
                                    {weekDays.map((day) => (
                                        <div key={`first-${day}`} className="text-center text-xs font-medium text-gray-600">
                                            {day.substring(0, 3)}
                                        </div>
                                    ))}
                                </div>

                                {/* Grade de dias */}
                                <div className="grid grid-cols-7 gap-1">
                                    {generateDaysForMonth(currentMonth)}
                                </div>
                            </div>

                            {/* Segundo calendário (mês seguinte) */}
                            <div className="w-72">
                                {/* Cabeçalho do segundo calendário */}
                                <div className="flex items-center justify-between mb-4">
                                    <button
                                        type="button"
                                        className="invisible p-1 rounded-full"
                                    >
                                        <ChevronLeftIcon />
                                    </button>

                                    <h3 className="text-base font-medium">
                                        {new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1).toLocaleDateString("pt-BR", {
                                            month: "long",
                                            year: "numeric",
                                        })}
                                    </h3>

                                    <button
                                        type="button"
                                        onClick={goToNextMonth}
                                        className="p-1 rounded-full hover:bg-gray-100"
                                    >
                                        <ChevronRightIcon />
                                    </button>
                                </div>

                                {/* Dias da semana */}
                                <div className="grid grid-cols-7 mb-2">
                                    {weekDays.map((day) => (
                                        <div key={`second-${day}`} className="text-center text-xs font-medium text-gray-600">
                                            {day.substring(0, 3)}
                                        </div>
                                    ))}
                                </div>

                                {/* Grade de dias do mês seguinte */}
                                <div className="grid grid-cols-7 gap-1">
                                    {generateDaysForMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Rodapé com ações */}
                    <div className="flex justify-center mt-4 pt-3 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={clearSelection}
                            className="text-sm text-red-600 hover:text-red-700"
                        >
                            Limpar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;